import React from "react";
import AdditionalBenefitsAdapter from "../../../../pages/additional-benefits/adapter";
import CardholderAdditionalBenefitsAdapter from "../../../../pages/cardholder-additional-benefits/adapter";
import CardholderBasicInformationAdapter from "../../../../pages/cardholder-basic-information/adapter";
import SigningAdapter from "../../../../pages/choose-signatories/adapter";
import GeneralCompanyInformationAdapter from "../../../../pages/general-company-information/adapter";
import HomeAdapter from "../../../../pages/home/adapter";
import PowerOfAttorneyAdapter from "../../../../pages/power-of-attorney/adapter";
import ConfirmationAdapter from "../../../../pages/sign/adapter";
import { ApplicationFlows, ApplicationFlowSteps } from "./types";

import SummaryAdapter from "../../../../pages/summary/adapter";
import logging from "../../../../util/logging";
import {
  BasicAccountApplicationDocument,
  CardApplicationDocument,
  SigningProcessDocument,
} from "../../../../api/commercial/generated-types";
import { DocumentUploadAdapter } from "../../../../pages/document-upload/adapter";
import { useFeatureToggles } from "../../../../pages/shared/hooks/useFeatureToggles";
import { Feature } from "../../../../context/types";
import { CustomizationCardAdapter } from "../../../../pages/document-upload/customization-card/adapter";
import { useCustomizedCardUploadStep } from "./useCustomizedCardUploadStep";
import { CompanyOnlineAdapter } from "../../../../pages/company-online/adapter";
import { RegulatoryInformationAdapter } from "../../../../pages/regulatory-information/adapter";

const log = logging(
  "route/application/authenticated/application-flow/currentFlowStep.tsx",
);

interface CurrentFlowStepProps {
  flowType: ApplicationFlows;
  basicAccountApplication: BasicAccountApplicationDocument;
  cardApplication: CardApplicationDocument;
  signingProcess?: SigningProcessDocument;
  triggerReload: () => void;
  step: ApplicationFlowSteps;
  setStep: (step: ApplicationFlowSteps) => void;
}

const CurrentFlowStep: React.FC<CurrentFlowStepProps> = ({
  flowType,
  basicAccountApplication,
  cardApplication,
  signingProcess,
  triggerReload,
  step,
  setStep,
}: CurrentFlowStepProps): React.JSX.Element => {
  const canUploadAttachments = useFeatureToggles(Feature.ATTACHMENT_UPLOAD);
  const hasCustomizedCard = useCustomizedCardUploadStep();

  if (step === ApplicationFlowSteps.COMPANY) {
    return (
      <HomeAdapter
        basicAccountApplication={basicAccountApplication}
        triggerReload={triggerReload}
      />
    );
  }
  if (step === ApplicationFlowSteps.BASIC_ACCOUNT_INFORMATION) {
    return (
      <GeneralCompanyInformationAdapter
        flowType={flowType}
        basicAccountApplication={basicAccountApplication}
        triggerReload={triggerReload}
        setStep={setStep}
      />
    );
  }

  if (step === ApplicationFlowSteps.BASIC_ACCOUNT_BENEFITS) {
    return (
      <AdditionalBenefitsAdapter
        basicAccountApplication={basicAccountApplication}
        triggerReload={triggerReload}
        setStep={setStep}
      />
    );
  }

  if (step === ApplicationFlowSteps.REGULATORY_INFORMATION) {
    return (
      <RegulatoryInformationAdapter
        basicAccountApplication={basicAccountApplication}
        triggerReload={triggerReload}
        setStep={setStep}
      />
    );
  }

  if (step === ApplicationFlowSteps.POWER_OF_ATTORNEY) {
    return (
      <PowerOfAttorneyAdapter
        basicAccountApplication={basicAccountApplication}
        triggerReload={triggerReload}
        setStep={setStep}
      />
    );
  }

  if (step === ApplicationFlowSteps.COMPANY_ONLINE) {
    return (
      <CompanyOnlineAdapter
        basicAccountApplication={basicAccountApplication}
        triggerReload={triggerReload}
        setStep={setStep}
      />
    );
  }

  if (step === ApplicationFlowSteps.CARDHOLDER) {
    return (
      <CardholderBasicInformationAdapter
        flowType={flowType}
        basicAccountApplication={basicAccountApplication}
        cardApplication={cardApplication}
        triggerReload={triggerReload}
        setStep={setStep}
      />
    );
  }

  if (step === ApplicationFlowSteps.CARDHOLDER_BENEFITS) {
    return (
      <CardholderAdditionalBenefitsAdapter
        flowType={flowType}
        cardApplication={cardApplication}
        triggerReload={triggerReload}
        setStep={setStep}
      />
    );
  }

  if (step === ApplicationFlowSteps.CHOOSE_SIGNATORIES) {
    return (
      <SigningAdapter
        flowType={flowType}
        cardApplication={cardApplication}
        triggerReload={triggerReload}
        setStep={setStep}
      />
    );
  }
  if (step === ApplicationFlowSteps.SUMMARY) {
    return (
      <SummaryAdapter
        flowType={flowType}
        cardApplication={cardApplication}
        triggerReload={triggerReload}
        setStep={setStep}
      />
    );
  }

  if (step === ApplicationFlowSteps.SIGN) {
    return (
      <ConfirmationAdapter
        flowType={flowType}
        basicAccountApplication={basicAccountApplication}
        cardApplication={cardApplication}
        signingProcess={signingProcess}
        setStep={setStep}
        triggerReload={triggerReload}
      />
    );
  }

  if (step === ApplicationFlowSteps.UPLOAD_ATTACHMENTS) {
    if (canUploadAttachments) {
      return (
        <DocumentUploadAdapter
          flowType={flowType}
          cardApplication={cardApplication}
          basicAccountApplication={basicAccountApplication}
          triggerReload={triggerReload}
        />
      );
    } else if (hasCustomizedCard) {
      /* If the feature attachmentUpload is disabled and the user has CustomizedCard they should be able to
      get the link for uploading a custom card */
      return (
        <CustomizationCardAdapter
          flowType={flowType}
          basicAccountApplication={basicAccountApplication}
          cardApplication={cardApplication}
          triggerReload={triggerReload}
        />
      );
    }
  }

  log.warn(`Unknown step in authenticated section: ${step}`);
};

export default CurrentFlowStep;
